
// Branded split-screen login — Live (password) vs Demo (passwordless sandbox)
import { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Loader2, Mail, KeyRound, ShieldCheck, Building2, ArrowRight, FlaskConical, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import prLogo from "@/assets/priority-risk-logo.png";

const HERO = process.env.REACT_APP_HERO_IMAGE || "https://customer-assets.emergentagent.com/job_risk-portal-dev/artifacts/r5e3ij1z_london-skyline.png";

const ROLE_LABELS = {
    director: "Director", administrator: "Administrator", consultant: "Consultant",
    client_admin: "Client Admin", site_manager: "Site Manager", reviewer: "Reviewer",
};
const ROLE_TONE = {
    director: "#C75A1B", administrator: "#2A5DB0", consultant: "#2E7D5B",
    client_admin: "#6B4E9E", site_manager: "#A8501F", reviewer: "#6F6A64",
};

export default function LoginPage() {
    const { user, login, demoLogin, forgotPassword } = useAuth();
    const [mode, setMode] = useState("live");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [forgot, setForgot] = useState(false);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [demoUsers, setDemoUsers] = useState([]);
    const [demoLoading, setDemoLoading] = useState(false);
    const [pendingId, setPendingId] = useState(null);
    const nav = useNavigate();

    const loadDemoUsers = useCallback(async () => {
        setErr("");
        setDemoLoading(true);
        // Cold start after a deploy: the demo personas may still be seeding (the
        // full sandbox fills in the background). Retry a few times with backoff
        // and a generous per-request timeout before showing an error, so a slow
        // first response never dead-ends the login page.
        const delays = [800, 1500, 2500, 4000];
        let attempt = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            try {
                const r = await api.get("/auth/demo-users", { timeout: 25000 });
                if (Array.isArray(r.data) && r.data.length > 0) {
                    setDemoUsers(r.data);
                    setDemoLoading(false);
                    return;
                }
                // Empty (still seeding) — fall through to retry.
            } catch (e) {
                // network/timeout/5xx — fall through to retry.
            }
            if (attempt >= delays.length) {
                setErr("Couldn't load demo accounts. The sandbox may still be starting up — please retry.");
                setDemoLoading(false);
                return;
            }
            await new Promise((res) => setTimeout(res, delays[attempt]));
            attempt += 1;
        }
    }, []);

    useEffect(() => {
        if (mode !== "demo") return;
        loadDemoUsers();
    }, [mode, loadDemoUsers]);

    // Surface a Microsoft SSO error passed back on the login URL (?error=...).
    useEffect(() => {
        const code = new URLSearchParams(window.location.search).get("error");
        if (!code) return;
        const MESSAGES = {
            no_account: "No Priority Risk account found for this Microsoft sign-in. An administrator needs to create your account with a role assigned before you can sign in.",
            account_inactive: "This account is deactivated. Contact your administrator to restore access.",
            sso_error: "Microsoft sign-in couldn't be completed. Please try again or use email and password.",
            sso_unconfigured: "Microsoft sign-in isn't configured yet. Please use email and password.",
        };
        setMode("live");
        setErr(MESSAGES[code] || "Microsoft sign-in failed. Please try again.");
        // Clean the URL so a refresh doesn't re-show the error.
        window.history.replaceState({}, "", "/login");
    }, []);

    if (user) return <Navigate to="/app/dashboard" replace />;

    async function onSubmit(e) {
        e.preventDefault();
        setErr("");
        setBusy(true);
        const res = await login(email.trim().toLowerCase(), password);
        setBusy(false);
        if (res.ok) nav("/app/dashboard", { replace: true });
        else setErr(res.error || "Could not sign in");
    }

    async function enterDemo(u) {
        setErr("");
        setPendingId(u.id);
        const res = await demoLogin(u.id);
        setPendingId(null);
        if (res.ok) nav("/app/dashboard", { replace: true });
        else setErr(res.error || "Could not enter demo");
    }

    function onMicrosoft() {
        window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/microsoft/login`;
    }

    return (
        <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#F5F3F0]">
            {/* Left art panel */}
            <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden"
                 style={{ background: "#1F1F1F" }}>
                <img
                    src={HERO}
                    alt="London skyline at sunset"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0"
                     style={{ background: "linear-gradient(160deg, rgba(26,26,26,.20) 0%, rgba(26,26,26,.55) 60%, rgba(26,26,26,.85) 100%)" }} />
                <div className="absolute inset-0"
                     style={{ background: "linear-gradient(90deg, transparent 50%, rgba(250,248,245,.0) 100%)" }} />

                <div className="relative z-10 flex items-center gap-3">
                    <img src={prLogo} alt="Priority Risk — Safety Takes Priority"
                        className="h-32 w-auto object-contain drop-shadow-lg" />
                </div>

                <div className="relative z-10 max-w-md">
                    <div className="font-serif text-[44px] leading-[1.05] text-white mb-3 tracking-tight drop-shadow-md">
                        One operating system for compliance, the <i style={{ color: "#FBA871" }}>Golden Thread</i>
                        {" "}and every site visit.
                    </div>
                    <div className="text-[14px] leading-relaxed mt-5" style={{ color: "rgba(255,255,255,.88)" }}>
                        CDM 2015 · Building Safety Act 2022 · Fire safety · Audits, actions and resident
                        engagement — for Priority Risk consultants and our clients.
                    </div>
                </div>

                <div className="relative z-10 grid grid-cols-2 gap-3 max-w-md">
                    <Feature icon={Building2} label="Multi-tenant" sub="Internal + branded portals" />
                    <Feature icon={ShieldCheck} label="Audit-trailed" sub="Every record. Every event." />
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-8">
                <div className="w-full max-w-[440px] animate-fade-in">
                    {/* Mode switch */}
                    <div className="flex p-1 rounded-[12px] bg-white border mb-7" style={{ borderColor: "#E5E0DA" }}>
                        <button
                            type="button"
                            data-testid={TID.loginModeLive}
                            onClick={() => { setMode("live"); setErr(""); setForgot(false); }}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-[9px] text-[12.5px] font-semibold transition-colors"
                            style={mode === "live" ? { background: "#C75A1B", color: "#fff" } : { color: "#6F6A64" }}
                        >
                            <ShieldCheck className="w-4 h-4" /> Live account
                        </button>
                        <button
                            type="button"
                            data-testid={TID.loginModeDemo}
                            onClick={() => { setMode("demo"); setErr(""); }}
                            className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-[9px] text-[12.5px] font-semibold transition-colors"
                            style={mode === "demo" ? { background: "#C75A1B", color: "#fff" } : { color: "#6F6A64" }}
                        >
                            <FlaskConical className="w-4 h-4" /> Demo sandbox
                        </button>
                    </div>

                    {mode === "live" ? (forgot ? (
                        <ForgotPasswordForm onBack={() => { setForgot(false); setErr(""); }} forgotPassword={forgotPassword} />
                    ) : (
                        <form onSubmit={onSubmit}>
                            <div className="mb-6">
                                <div className="text-[11px] uppercase tracking-[.2em] font-bold mb-2" style={{ color: "#9A968F" }}>
                                    Sign in
                                </div>
                                <h1 className="font-display text-3xl font-bold text-[#1F1F1F]">Welcome back.</h1>
                                <p className="text-[13px] mt-2" style={{ color: "#6F6A64" }}>
                                    Sign in with your Priority Risk account or branded portal credentials.
                                </p>
                            </div>

                            <label className="block">
                                <span className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#6F6A64" }}>Email</span>
                                <div className="flex items-center gap-2 px-3.5 py-2.5 bg-white border rounded-[10px] focus-within:border-[#C75A1B] focus-within:ring-2 focus-within:ring-[#FBEFE5] transition-colors" style={{ borderColor: "#E5E0DA" }}>
                                    <Mail className="w-4 h-4" style={{ color: "#9A968F" }} />
                                    <input data-testid={TID.loginEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className="flex-1 outline-none bg-transparent text-[13px] text-[#1F1F1F]" />
                                </div>
                            </label>

                            <label className="block mt-3.5">
                                <span className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#6F6A64" }}>
                                    <span>Password</span>
                                    <button type="button" onClick={() => { setForgot(true); setErr(""); }} className="text-[10.5px] normal-case text-[#C75A1B] hover:underline">Forgot?</button>
                                </span>
                                <div className="flex items-center gap-2 px-3.5 py-2.5 bg-white border rounded-[10px] focus-within:border-[#C75A1B] focus-within:ring-2 focus-within:ring-[#FBEFE5] transition-colors" style={{ borderColor: "#E5E0DA" }}>
                                    <KeyRound className="w-4 h-4" style={{ color: "#9A968F" }} />
                                    <input data-testid={TID.loginPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" className="flex-1 outline-none bg-transparent text-[13px] text-[#1F1F1F]" />
                                </div>
                            </label>

                            {err && (
                                <div data-testid={TID.loginError} className="mt-4 px-3.5 py-2.5 rounded-[10px] text-[12.5px]" style={{ background: "#FBE8E5", color: "#B33A2E", border: "1px solid #F2D0CA" }}>
                                    {err}
                                </div>
                            )}

                            <button type="submit" data-testid={TID.loginSubmit} disabled={busy}
                                className="mt-5 w-full py-3 rounded-[10px] text-[13px] font-semibold text-white inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                                style={{ background: "#C75A1B" }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = "#9F4212")}
                                onMouseLeave={(e) => (e.currentTarget.style.background = "#C75A1B")}>
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                                {busy ? "Signing in…" : "Sign in"}
                            </button>

                            <div className="my-5 relative">
                                <div className="absolute inset-0 flex items-center"><div className="w-full border-t" style={{ borderColor: "#E5E0DA" }} /></div>
                                <div className="relative flex justify-center">
                                    <span className="px-3 text-[10.5px] uppercase tracking-wider bg-[#F5F3F0]" style={{ color: "#9A968F" }}>or</span>
                                </div>
                            </div>

                            <button type="button" data-testid={TID.loginMicrosoft} onClick={onMicrosoft}
                                className="w-full py-3 rounded-[10px] text-[13px] font-semibold bg-white border inline-flex items-center justify-center gap-3 transition-colors hover:border-[#1F1F1F]"
                                style={{ borderColor: "#E5E0DA", color: "#1F1F1F" }}>
                                <MicrosoftLogo />
                                Sign in with Microsoft
                            </button>
                            <p className="text-[10.5px] mt-2 text-center italic" style={{ color: "#9A968F" }}>
                                Staff sign-in with your Priority Risk Microsoft account.
                            </p>
                        </form>
                    )) : (
                        <div data-testid="demo-picker">
                            <div className="mb-5">
                                <div className="text-[11px] uppercase tracking-[.2em] font-bold mb-2 inline-flex items-center gap-2" style={{ color: "#C75A1B" }}>
                                    <FlaskConical className="w-3.5 h-3.5" /> Demo sandbox
                                </div>
                                <h1 className="font-display text-3xl font-bold text-[#1F1F1F]">Try any role.</h1>
                                <p className="text-[13px] mt-2" style={{ color: "#6F6A64" }}>
                                    No password needed. Pick a user to explore the platform with sample data — changes here never touch live.
                                </p>
                            </div>

                            {err && (
                                <div data-testid={TID.loginError} className="mb-4 px-3.5 py-2.5 rounded-[10px] text-[12.5px] flex items-center justify-between gap-3" style={{ background: "#FBE8E5", color: "#B33A2E", border: "1px solid #F2D0CA" }}>
                                    <span>{err}</span>
                                    <button
                                        data-testid="demo-retry-button"
                                        onClick={loadDemoUsers}
                                        className="shrink-0 font-semibold underline hover:no-underline"
                                    >
                                        Retry
                                    </button>
                                </div>
                            )}

                            {demoLoading ? (
                                <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#C75A1B" }} /></div>
                            ) : (
                                <div className="space-y-2 max-h-[46vh] overflow-y-auto pr-1">
                                    {demoUsers.map((u) => (
                                        <button
                                            key={u.id}
                                            data-testid={TID.demoPersona(u.role)}
                                            onClick={() => enterDemo(u)}
                                            disabled={pendingId !== null}
                                            className="w-full flex items-center gap-3 px-3.5 py-3 bg-white border rounded-[11px] text-left transition-all hover:shadow-brand hover:-translate-y-px disabled:opacity-60"
                                            style={{ borderColor: "#E5E0DA" }}
                                        >
                                            <span className="w-10 h-10 rounded-[9px] grid place-items-center text-[12px] font-bold text-white shrink-0" style={{ background: ROLE_TONE[u.role] || "#6F6A64" }}>
                                                {u.avatar_initials || u.name?.slice(0, 2).toUpperCase()}
                                            </span>
                                            <div className="min-w-0 flex-1">
                                                <div className="text-[13px] font-semibold text-[#1F1F1F] truncate">{u.name}</div>
                                                <div className="text-[11px] truncate" style={{ color: "#6F6A64" }}>
                                                    {u.title || u.email}{u.client_name ? ` · ${u.client_name}` : ""}
                                                </div>
                                            </div>
                                            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0" style={{ background: "#F6F4F0", color: ROLE_TONE[u.role] || "#6F6A64" }}>
                                                {ROLE_LABELS[u.role] || u.role}
                                            </span>
                                            {pendingId === u.id ? <Loader2 className="w-4 h-4 animate-spin shrink-0" style={{ color: "#C75A1B" }} /> : <ArrowRight className="w-4 h-4 shrink-0" style={{ color: "#C7C2BB" }} />}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <p className="text-[10.5px] mt-4 text-center italic inline-flex items-center justify-center gap-1 w-full" style={{ color: "#9A968F" }}>
                                <Users className="w-3 h-3" /> Switch users anytime from the demo banner once inside.
                            </p>
                        </div>
                    )}

                    <div className="mt-7 text-[10.5px] text-center" style={{ color: "#9A968F" }}>
                        © Priority Risk · Live + Demo environments
                    </div>
                </div>
            </div>
        </div>
    );
}

function Feature({ icon: Icon, label, sub }) {
    return (
        <div className="rounded-[10px] px-3.5 py-3 backdrop-blur-md border"
             style={{ background: "rgba(26,26,26,.45)", borderColor: "rgba(255,255,255,.18)" }}>
            <div className="flex items-center gap-2">
                <Icon className="w-4 h-4" style={{ color: "#FBA871" }} />
                <span className="text-[12px] font-semibold text-white">{label}</span>
            </div>
            <div className="text-[10.5px] mt-1" style={{ color: "rgba(255,255,255,.72)" }}>{sub}</div>
        </div>
    );
}

function MicrosoftLogo() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <rect x="1" y="1" width="6.5" height="6.5" fill="#F25022" />
            <rect x="8.5" y="1" width="6.5" height="6.5" fill="#7FBA00" />
            <rect x="1" y="8.5" width="6.5" height="6.5" fill="#00A4EF" />
            <rect x="8.5" y="8.5" width="6.5" height="6.5" fill="#FFB900" />
        </svg>
    );
}

function ForgotPasswordForm({ onBack, forgotPassword }) {
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const [sent, setSent] = useState(false);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        await forgotPassword(email.trim().toLowerCase());
        setBusy(false);
        setSent(true);
    }

    return (
        <div data-testid="forgot-password-form">
            <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[.2em] font-bold mb-2" style={{ color: "#9A968F" }}>Reset access</div>
                <h1 className="font-display text-3xl font-bold text-[#1F1F1F]">Forgot password?</h1>
                <p className="text-[13px] mt-2" style={{ color: "#6F6A64" }}>
                    Enter your account email and we'll send you a secure link to reset your password.
                </p>
            </div>
            {sent ? (
                <div data-testid="forgot-sent" className="px-3.5 py-3 rounded-[10px] text-[12.5px]" style={{ background: "#E8F1E6", color: "#3F7D3A", border: "1px solid #CFE4CB" }}>
                    If that email is registered, a reset link is on its way. Check your inbox (and spam).
                </div>
            ) : (
                <form onSubmit={submit}>
                    <label className="block">
                        <span className="block text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: "#6F6A64" }}>Email</span>
                        <div className="flex items-center gap-2 px-3.5 py-2.5 bg-white border rounded-[10px] focus-within:border-[#C75A1B] focus-within:ring-2 focus-within:ring-[#FBEFE5]" style={{ borderColor: "#E5E0DA" }}>
                            <Mail className="w-4 h-4" style={{ color: "#9A968F" }} />
                            <input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email" className="flex-1 outline-none bg-transparent text-[13px] text-[#1F1F1F]" />
                        </div>
                    </label>
                    <button type="submit" data-testid="forgot-submit" disabled={busy}
                        className="mt-5 w-full py-3 rounded-[10px] text-[13px] font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "#C75A1B" }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} Send reset link
                    </button>
                </form>
            )}
            <button onClick={onBack} data-testid="forgot-back" className="mt-4 w-full text-[12px] font-semibold text-[#6F6A64] hover:underline">
                Back to sign in
            </button>
        </div>
    );
}
